
import * as React from "react"
import {
  Dialog as ShadcnDialog,
  DialogContent as ShadcnDialogContent,
  DialogHeader as ShadcnDialogHeader,
  DialogTitle as ShadcnDialogTitle,
  DialogDescription as ShadcnDialogDescription,
  DialogFooter as ShadcnDialogFooter
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

export interface EnhancedDialogProps extends React.ComponentPropsWithoutRef<typeof ShadcnDialog> {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
}

export const EnhancedDialog = ({ children, ...props }: EnhancedDialogProps) => {
  return (
    <ShadcnDialog {...props}>
      {children}
    </ShadcnDialog>
  );
};
EnhancedDialog.displayName = "EnhancedDialog";

export interface EnhancedDialogContentProps extends React.ComponentPropsWithoutRef<typeof ShadcnDialogContent> {
  children?: React.ReactNode;
  className?: string;
}

export const EnhancedDialogContent = React.forwardRef<HTMLDivElement, EnhancedDialogContentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ShadcnDialogContent 
        ref={ref} 
        className={cn(className)} 
        {...props}
      >
        {children}
      </ShadcnDialogContent>
    );
  }
);
EnhancedDialogContent.displayName = "EnhancedDialogContent";

export interface EnhancedDialogHeaderProps extends React.ComponentPropsWithoutRef<typeof ShadcnDialogHeader> {
  children?: React.ReactNode;
  className?: string;
}

export const EnhancedDialogHeader = ({ className, children, ...props }: EnhancedDialogHeaderProps) => {
  return (
    <ShadcnDialogHeader className={cn(className)} {...props}>
      {children}
    </ShadcnDialogHeader>
  );
};
EnhancedDialogHeader.displayName = "EnhancedDialogHeader";

export interface EnhancedDialogTitleProps extends React.ComponentPropsWithoutRef<typeof ShadcnDialogTitle> {
  children?: React.ReactNode;
  className?: string;
}

export const EnhancedDialogTitle = React.forwardRef<HTMLHeadingElement, EnhancedDialogTitleProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ShadcnDialogTitle 
        ref={ref} 
        className={cn(className)} 
        {...props}
      >
        {children}
      </ShadcnDialogTitle>
    );
  }
);
EnhancedDialogTitle.displayName = "EnhancedDialogTitle";

export interface EnhancedDialogDescriptionProps extends React.ComponentPropsWithoutRef<typeof ShadcnDialogDescription> {
  children?: React.ReactNode;
  className?: string;
}

export const EnhancedDialogDescription = React.forwardRef<HTMLParagraphElement, EnhancedDialogDescriptionProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ShadcnDialogDescription 
        ref={ref} 
        className={cn(className)} 
        {...props}
      >
        {children}
      </ShadcnDialogDescription>
    );
  }
);
EnhancedDialogDescription.displayName = "EnhancedDialogDescription";

export interface EnhancedDialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  className?: string;
}

export const EnhancedDialogFooter = ({ className, children, ...props }: EnhancedDialogFooterProps) => {
  return (
    <ShadcnDialogFooter className={cn(className)} {...props}>
      {children}
    </ShadcnDialogFooter>
  );
};
EnhancedDialogFooter.displayName = "EnhancedDialogFooter";

// Export the enhanced components as the default names
export const Dialog = EnhancedDialog;
export const DialogContent = EnhancedDialogContent;
export const DialogHeader = EnhancedDialogHeader;
export const DialogTitle = EnhancedDialogTitle;
export const DialogDescription = EnhancedDialogDescription;
export const DialogFooter = EnhancedDialogFooter;
